import { useEffect, useRef, useState } from "react";
import { ExternalLink, Search } from "lucide-react";

/**
 * MLS iframe with a graceful fallback if the remote site blocks embedding
 * via X-Frame-Options or CSP. Shared between the agent's MLS Search page
 * and the client portal — same UX so behavior is predictable everywhere.
 *
 * Detection is best-effort: cross-origin `contentDocument` access throws
 * (that's the GOOD outcome — content loaded), empty same-origin doc is
 * the BAD outcome. A 6-second safety-net timer catches cases where the
 * onLoad event never fires because the browser aborted the frame.
 */
export default function MlsIframeWithFallback({ url, title = "MLS Search", height = "70vh", minHeight = 480 }) {
  const ref = useRef(null);
  const [status, setStatus] = useState("unknown");

  useEffect(() => {
    if (!url) return;
    setStatus("unknown");
    const t = setTimeout(() => setStatus(prev => (prev === "unknown" ? "blocked" : prev)), 6000);
    return () => clearTimeout(t);
  }, [url]);

  const onLoad = () => {
    try {
      const doc = ref.current?.contentDocument;
      if (doc && (!doc.body || doc.body.children.length === 0)) {
        setStatus("blocked");
        return;
      }
    } catch { /* cross-origin, content loaded */ }
    setStatus("ok");
  };

  if (!url) return null;
  const blocked = status === "blocked";
  return (
    <div className="relative" style={{ height, minHeight }} data-testid="mls-frame-with-fallback">
      <iframe
        ref={ref}
        src={url}
        title={title}
        className="w-full h-full block"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
        onLoad={onLoad}
        data-testid="mls-iframe"
      />
      {blocked && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-50/95 backdrop-blur-sm p-6" data-testid="mls-blocked-fallback">
          <div className="max-w-sm text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Search className="h-6 w-6 text-amber-800" />
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-slate-900">This MLS site blocks embedding</h3>
            <p className="mt-1 text-sm text-slate-600">
              Their security won't let us show the search inline. Open it in a new tab — everything else in the portal still works normally.
            </p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold text-white shadow-sm"
              style={{ background: "var(--primary-hex)" }}
              data-testid="mls-launch-new-tab-btn"
            >
              <ExternalLink className="h-4 w-4" /> Launch MLS Search in New Tab
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
